import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { authService } from '../services/authService';
import presenceService from '../services/presenceService';
import { settingsService } from '../services/settingsService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const heartbeatIntervalRef = useRef(null);
  const logoutTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const logoutTimeoutMinutesRef = useRef(0); // 0 = nunca se desloguea

  // Verificar autenticación al cargar
  useEffect(() => {
    checkAuth();
  }, []);

  // Escuchar eventos de token expirado
  useEffect(() => {
    let isHandlingTokenExpired = false; // Flag para evitar múltiples llamadas simultáneas
    
    const handleTokenExpired = () => {
      // Evitar múltiples llamadas simultáneas
      if (isHandlingTokenExpired) {
        return;
      }
      
      isHandlingTokenExpired = true;
      console.log('Token expirado, cerrando sesión y redirigiendo al login...');
      
      // Limpiar todo el estado de autenticación
      stopHeartbeat();
      
      // Limpiar token primero para evitar que logout intente llamar al servidor
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      
      // Limpiar estado local sin llamar a authService.logout() (que intentaría llamar al servidor)
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false); // Asegurar que no quede en estado de carga
      
      // Resetear flag después de un breve delay
      setTimeout(() => {
        isHandlingTokenExpired = false;
      }, 1000);
    };

    window.addEventListener('auth:token-expired', handleTokenExpired);
    return () => {
      window.removeEventListener('auth:token-expired', handleTokenExpired);
    };
  }, []);

  // Iniciar/Detener heartbeat cuando cambia el estado de autenticación
  useEffect(() => {
    if (isAuthenticated && user) {
      startHeartbeat();
    } else {
      stopHeartbeat();
    }

    return () => {
      stopHeartbeat();
    };
  }, [isAuthenticated, user]);

  // Escuchar cambios de visibilidad del documento
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isAuthenticated && user) {
        // Si el documento se vuelve visible, enviar actividad (marca como online)
        presenceService.sendActivity();
        // Reiniciar timer de deslogueo cuando el documento vuelve a ser visible
        resetLogoutTimer();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, user]);

  // Cargar timeout de deslogueo cuando el usuario se autentica
  useEffect(() => {
    if (isAuthenticated && user) {
      loadLogoutTimeout();
    } else {
      // Limpiar timeout si no está autenticado
      clearLogoutTimeout();
    }
  }, [isAuthenticated, user]);

  const loadLogoutTimeout = async () => {
    try {
      const timeout = await settingsService.getLogoutTimeout();
      logoutTimeoutMinutesRef.current = timeout;
      console.log(`[AUTH CONTEXT] Timeout de deslogueo cargado: ${timeout} minutos (${timeout === 0 ? 'nunca se desloguea' : `${timeout} min de inactividad`})`);
      // Reiniciar el timer con el nuevo timeout
      resetLogoutTimer();
    } catch (error) {
      console.error('[AUTH CONTEXT] Error cargando timeout de deslogueo:', error);
      // Por defecto, nunca se desloguea si hay error
      logoutTimeoutMinutesRef.current = 0;
    }
  };

  const resetLogoutTimer = () => {
    // Limpiar timer anterior
    clearLogoutTimeout();
    
    // Si el timeout es 0, nunca se desloguea
    if (logoutTimeoutMinutesRef.current === 0) {
      console.log('[AUTH CONTEXT] Timeout de deslogueo deshabilitado (0 minutos)');
      return;
    }
    
    // Actualizar última actividad
    lastActivityRef.current = Date.now();
    
    // Configurar nuevo timer
    const timeoutMs = logoutTimeoutMinutesRef.current * 60 * 1000; // Convertir minutos a milisegundos
    logoutTimeoutRef.current = setTimeout(() => {
      console.log(`[AUTH CONTEXT] Timeout de deslogueo alcanzado (${logoutTimeoutMinutesRef.current} minutos de inactividad)`);
      handleAutoLogout();
    }, timeoutMs);
    
    console.log(`[AUTH CONTEXT] Timer de deslogueo reiniciado: ${logoutTimeoutMinutesRef.current} minutos`);
  };

  const clearLogoutTimeout = () => {
    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
      logoutTimeoutRef.current = null;
    }
  };

  const handleAutoLogout = async () => {
    console.log('[AUTH CONTEXT] Deslogueo automático por inactividad');
    clearLogoutTimeout();
    stopHeartbeat();
    await logout();
    // Mostrar mensaje al usuario
    alert(`Tu sesión ha expirado por inactividad (${logoutTimeoutMinutesRef.current} minutos). Por favor, inicia sesión nuevamente.`);
  };

  // Escuchar eventos de actividad del usuario (click, keydown, navegación)
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const handleActivity = () => {
      // Solo enviar si el documento está visible
      if (document.visibilityState === 'visible') {
        presenceService.sendActivity();
        // Reiniciar timer de deslogueo cuando hay actividad
        resetLogoutTimer();
      }
    };

    // Eventos que indican actividad real
    document.addEventListener('click', handleActivity, true);
    document.addEventListener('keydown', handleActivity, true);
    
    // Navegación interna (cambios de ruta)
    const handleNavigation = () => {
      if (document.visibilityState === 'visible') {
        presenceService.sendActivity();
        // Reiniciar timer de deslogueo cuando hay navegación
        resetLogoutTimer();
      }
    };
    window.addEventListener('popstate', handleNavigation);

    return () => {
      document.removeEventListener('click', handleActivity, true);
      document.removeEventListener('keydown', handleActivity, true);
      window.removeEventListener('popstate', handleNavigation);
    };
  }, [isAuthenticated, user]);

  const startHeartbeat = () => {
    // Limpiar intervalo anterior si existe
    stopHeartbeat();

    // Enviar heartbeat inmediatamente
    presenceService.sendHeartbeat();

    // Configurar intervalo de 30 segundos
    heartbeatIntervalRef.current = setInterval(() => {
      presenceService.sendHeartbeat();
    }, 30000); // 30 segundos

    console.log('[AUTH CONTEXT] Heartbeat iniciado');
  };

  const stopHeartbeat = () => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
      console.log('[AUTH CONTEXT] Heartbeat detenido');
    }
  };

  const checkAuth = async () => {
    try {
      if (authService.isAuthenticated()) {
        const userData = await authService.getCurrentUser();
        setUser(userData);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Error verificando autenticación:', error);
      authService.logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await authService.login(email, password);
      
      // Verificar que el login fue exitoso
      if (!response || !response.access_token) {
        return { success: false, error: 'No se recibió un token de acceso' };
      }
      
      // Intentar obtener los datos del usuario
      try {
        const userData = await authService.getCurrentUser();
        setUser(userData);
        setIsAuthenticated(true);
        return { success: true, user: userData };
      } catch (userError) {
        // Si falla obtener el usuario pero tenemos token, aún consideramos el login exitoso
        console.warn('Error obteniendo datos del usuario:', userError);
        setIsAuthenticated(true);
        return { success: true, user: null };
      }
    } catch (error) {
      console.error('Error en login:', error);
      return { success: false, error: error.message || 'Error al iniciar sesión' };
    }
  };

  const logout = async () => {
    // Limpiar timer de deslogueo
    clearLogoutTimeout();
    
    // Detener heartbeat antes de hacer logout
    stopHeartbeat();
    
    // Llamar al endpoint de logout en el backend
    try {
      await authService.logout();
    } catch (error) {
      console.error('[AUTH CONTEXT] Error en logout del servidor:', error);
      // Continuar con el logout local aunque falle el servidor
    }
    
    // Limpiar estado local
    authService.logout();
    setUser(null);
    setIsAuthenticated(false);
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};

